import callsWidgetService from '@/modules/widgets/callsWidget/services/callsWidgetService';
import clientService from '@/modules/client/services/clientService';

const transformCallForDisplay = (call, rootGetters) => {
  const client = rootGetters['ClientStore/client'](call.client_uid);
  const clientName = client?.full_name || null;

  let staffName = null;
  let appName = null;

  if (call.status === 'ANSWERED_BY_STAFF' && call.staff_uid) {
    const staff = rootGetters['StaffStore/staffDataByUid'](call.staff_uid);
    staffName = staff?.display_name || staff?.first_name || null;
  }

  if (call.status === 'ANSWERED_BY_CALLS_PROVIDER' && call.external_app_name) {
    const externalApp = rootGetters['AppsStore/getMarketAppByName'](call.external_app_name);
    appName = externalApp?.app_name || null;
  }

  return {
    id: call.uid,
    caller_name: clientName,
    caller_number: call.from_number,
    status: call.status.toLowerCase(),
    original_status: call.status,
    staff_name: staffName,
    staff_uid: call.staff_uid,
    app_name: appName,
    external_app_name: call.external_app_name,
    created_at: call.created_at,
    client_uid: call.client_uid,
  };
};

const loadMarketAppsIfNeeded = ({ voiceCalls, dispatch }) => {
  const hasCallsProviderStatus = voiceCalls.some(
    (call) => call.status === 'ANSWERED_BY_CALLS_PROVIDER' && call.external_app_name,
  );

  if (hasCallsProviderStatus) {
    dispatch('AppsStore/getMarketApps', {}, { root: true }).catch((error) => {
      console.error('There was an error retrieving market apps: ', error);
    });
  }
};

const state = {
  callStats: null,
  recentCalls: null,
  missedCallsClientCount: null,
  isLoadingStats: false,
  isLoadingCalls: false,
  isLoadingMissedCallsCount: false,
};

const getters = {
  callStats: (state) => state.callStats,
  recentCalls: (state, localGetters, rootState, rootGetters) => {
    if (state.recentCalls?.voice_calls) {
      return state.recentCalls.voice_calls.map((call) => transformCallForDisplay(call, rootGetters));
    }
    return state.recentCalls || [];
  },
  isLoadingStats: (state) => state.isLoadingStats,
  isLoadingCalls: (state) => state.isLoadingCalls,
  totalCalls: (state) => state.callStats?.total_voice_calls || 0,
  missedCallsPending: (state) => {
    // Use client count if available, otherwise fall back to call stats
    if (state.missedCallsClientCount !== null) {
      return state.missedCallsClientCount;
    }
    // Find MISSED status count from status_counts array
    const missedStatus = state.callStats?.status_counts?.find((status) => status.status === 'MISSED');
    return missedStatus?.count || 0;
  },
  isLoadingMissedCallsCount: (state) => state.isLoadingMissedCallsCount,
};

const mutations = {
  setCallStats(state, data) {
    state.callStats = data;
  },
  setRecentCalls(state, data) {
    state.recentCalls = data;
  },
  setIsLoadingStats(state, loading) {
    state.isLoadingStats = loading;
  },
  setIsLoadingCalls(state, loading) {
    state.isLoadingCalls = loading;
  },
  setMissedCallsClientCount(state, count) {
    state.missedCallsClientCount = count;
  },
  setIsLoadingMissedCallsCount(state, loading) {
    state.isLoadingMissedCallsCount = loading;
  },
};

const actions = {
  async fetchCallStats({ commit }, timeRange = {}) {
    commit('setIsLoadingStats', true);
    try {
      const response = await callsWidgetService.getCallStats(timeRange);

      if (response && response.success) {
        commit('setCallStats', response.data);
        return true;
      }
      commit('setCallStats', null);
      return false;
    } catch (error) {
      console.error('There was an error retrieving call stats: ', error);
      commit('setCallStats', null);
      return false;
    } finally {
      commit('setIsLoadingStats', false);
    }
  },

  async fetchRecentCalls({ commit, dispatch }, params = {}) {
    commit('setIsLoadingCalls', true);
    try {
      const response = await callsWidgetService.getVoiceCalls(params);

      if (response && response.success && response.data?.voice_calls) {
        // Extract unique client UIDs
        const uniqueClientUids = [...new Set(response.data.voice_calls.map((call) => call.client_uid).filter(Boolean))];

        // Extract unique staff UIDs for calls answered by staff
        const uniqueStaffUids = [...new Set(
          response.data.voice_calls
            .filter((call) => call.status === 'ANSWERED_BY_STAFF' && call.staff_uid)
            .map((call) => call.staff_uid),
        )];

        // Fetch clients and staff in parallel
        await Promise.all([
          // Fetch each client from ClientStore
          ...uniqueClientUids.map(async (clientUid) => {
            try {
              await dispatch('ClientStore/getClient', clientUid, { root: true });
            } catch (error) {
              console.error('There was an error retrieving client: ', error);
            }
          }),
          // Fetch each staff member from StaffStore
          ...uniqueStaffUids.map(async (staffUid) => {
            try {
              await dispatch('StaffStore/fetchStaffDataByUid', staffUid, { root: true });
            } catch (error) {
              console.error('There was an error retrieving staff: ', error);
            }
          }),
        ]);

        commit('setRecentCalls', response.data);

        // Load market apps in background if needed (for ai_provider app name display)
        loadMarketAppsIfNeeded({
          voiceCalls: response.data.voice_calls,
          dispatch,
        });
        return true;
      }
      commit('setRecentCalls', null);
      return false;
    } catch (error) {
      console.error('There was an error retrieving recent calls: ', error);
      commit('setRecentCalls', null);
      return false;
    } finally {
      commit('setIsLoadingCalls', false);
    }
  },

  async fetchMissedCallsClientCount({ commit }, timeRange = {}) {
    commit('setIsLoadingMissedCallsCount', true);
    try {
      const { startDate, endDate } = timeRange || {};

      // Build search filters for clients with missed calls
      const filterParams = {
        search_filter: {
          matter_metadata_flat: {
            'voice_call.missed': {
              range: { gte: 1 },
            },
            'last_activity.message.voice_call.general': {
              range: { gte: 1 },
            },
          },
          text_filter: '',
        },
      };

      // Add date range filter if provided
      if (startDate && endDate) {
        filterParams.search_filter.last_interaction_filter = {
          range: {
            gte: startDate,
            lte: endDate,
          },
        };
      }

      const response = await clientService.getClients(
        0, // per_page = 0 to get only count
        0, // page
        '', // query
        '', // sortBy
        '', // selectedSorting
        '', // sortOrder
        filterParams,
      );

      const count = response.count || 0;
      commit('setMissedCallsClientCount', count);
      return count;
    } catch (error) {
      console.error('There was an error retrieving missed calls client count: ', error);
      commit('setMissedCallsClientCount', 0);
      return 0;
    } finally {
      commit('setIsLoadingMissedCallsCount', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
